import React, { useState, useEffect } from "react";
import { Table, Button, Card, Modal } from "react-bootstrap";
import EditRoleModal from "./EditRoleModal";
import AssignTaskModal from "./AssignTaskModal";
import { getCurrentAuthenticatedUser, getMemberData, fetchPaymentsForMember, deletePaymentById } from "./../../api/api";

function MembersTable({ members, refreshMembers, availableGroups, currentPage, membersPerPage }) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getCurrentAuthenticatedUser();
      const currentUser = await getMemberData(user.username);
      setCurrentUser(currentUser);
    };
    fetchUser();
  }, []);

  const handleEditRole = (member) => {
    setSelectedMember(member);
    setShowEditRoleModal(true);
  };

  const handleAssignTask = (member) => {
    setSelectedMember(member);
    setShowAssignTaskModal(true);
  };

  const handleViewPayments = async (member) => {
    try {
      const payments = await fetchPaymentsForMember(member.id);
      setPaymentHistory(payments);
      setShowPaymentsModal(true);
    } catch (error) {
      console.error("Error loading payments:", error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this payment?");
    if (!confirmDelete) return;

    try {
      await deletePaymentById(paymentId);
      setPaymentHistory((prev) => prev.filter((payment) => payment.id !== paymentId));
      alert("Payment deleted successfully.");
    } catch (error) {
      console.error("Error deleting payment:", error);
      alert("Failed to delete the payment. Please try again.");
    }
  };

  const handleCloseModal = () => {
    setShowEditRoleModal(false);
    setShowAssignTaskModal(false);
    setSelectedMember(null);
  };

  const handleClosePaymentsModal = () => {
    setShowPaymentsModal(false);
    setPaymentHistory([]);
  };

  const toggleExpand = (memberId) => {
    setExpandedMemberId(expandedMemberId === memberId ? null : memberId);
  };

  return (
    <div>
      {/* Desktop view */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <Table bordered>
            <thead>
              <tr>
                <th>#</th>
                <th>ProfilePic</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Gender</th>
                <th>Country</th>
                <th>City</th>
                <th>Profession</th>
                <th>Membership Status</th>
                <th>Role</th>
                <th>Task</th>
                <th>Payments</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member, index) => (
                <tr key={member.id}>
                  <td>{(currentPage - 1) * membersPerPage + index + 1}</td>
                  <td>
                    <img
                      src={member.profilePic}
                      alt="ProfilePic"
                      style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                    />
                  </td>
                  <td>{member.firstName}</td>
                  <td>{member.lastName}</td>
                  <td>{member.email}</td>
                  <td>{member.phoneNumber}</td>
                  <td>{member.gender}</td>
                  <td>{member.country}</td>
                  <td>{member.city}</td>
                  <td>{member.profession}</td>
                  <td>{member.membershipStatus}</td>
                  <td>
                    <Button variant="link" onClick={() => handleEditRole(member)}>
                      Edit Role
                    </Button>
                  </td>
                  <td>
                    <Button variant="link" onClick={() => handleAssignTask(member)}>
                      Assign Task
                    </Button>
                  </td>
                  <td>
                    <Button variant="link" onClick={() => handleViewPayments(member)}>
                      View Payments
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Mobile view */}
      <div className="d-block d-md-none">
        {members.map((member, index) => (
          <Card key={member.id} className="mb-3">
            <Card.Header onClick={() => toggleExpand(member.id)} style={{ cursor: "pointer" }}>
              <div className="d-flex align-items-center">
                <span>{(currentPage - 1) * membersPerPage + index + 1}.&nbsp;</span>
                <img
                  src={member.profilePic}
                  alt="ProfilePic"
                  style={{ width: "50px", height: "50px", borderRadius: "50%", marginRight: "10px" }}
                />
                <div>
                  {member.firstName} {member.lastName} - {member.country}
                </div>
              </div>
            </Card.Header>
            {expandedMemberId === member.id && (
              <Card.Body>
                <p>Email: {member.email}</p>
                <p>Phone: {member.phoneNumber}</p>
                <p>Gender: {member.gender}</p>
                <p>City: {member.city}</p>
                <p>Profession: {member.profession}</p>
                <p>Membership Status: {member.membershipStatus}</p>
                <Button variant="link" onClick={() => handleEditRole(member)}>
                  Edit Role
                </Button>
                <Button variant="link" onClick={() => handleAssignTask(member)}>
                  Assign Task
                </Button>
                <Button variant="link" onClick={() => handleViewPayments(member)}>
                  View Payments
                </Button>
              </Card.Body>
            )}
          </Card>
        ))}
      </div>

      {/* Modals */}
      {selectedMember && (
        <>
          <EditRoleModal
            show={showEditRoleModal}
            handleClose={handleCloseModal}
            member={selectedMember}
            refreshMembers={refreshMembers}
            availableGroups={availableGroups}
          />

          <AssignTaskModal
            show={showAssignTaskModal}
            handleClose={handleCloseModal}
            member={selectedMember}
            refreshMembers={refreshMembers}
            currentUser={currentUser}
          />
        </>
      )}

      {/* Payments Modal */}
      <Modal show={showPaymentsModal} onHide={handleClosePaymentsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payment History</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {paymentHistory.length > 0 ? (
            <Table bordered>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount (USD)</th>
                  <th>Memo</th>
                  <th>Payment Type</th>
                  <th>Status</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.paymentDate || "N/A"}</td>
                    <td>{payment.amountInUSD}</td>
                    <td>{payment.memo}</td>
                    <td>{payment.paymentType}</td>
                    <td>{payment.status}</td>
                    {/* <td>
                      <Button variant="danger" size="sm" onClick={() => handleDeletePayment(payment.id)}>
                        Delete
                      </Button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No payment history available.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MembersTable;